/**
 * BabylonScene utility functions and constants.
 * Extracted from BabylonScene.js to reduce file complexity.
 */
import * as BABYLON from '@babylonjs/core';

// ============================================
// MAPPING MATTER-STYLE PROJECTION SYSTEM
// ============================================
// Supports 100+ projectors using texture-based data storage
// Bypasses WebGL uniform/spotlight limits completely

export const MAX_PROJECTORS = 256;
export const PROJECTION_TEXTURE_SIZE = 512;
export const MAX_SPOTLIGHT_COUNT = 0;
export const AMBIENT_LIGHT_INTENSITY = 0.4;
export const BEAM_WIREFRAME_THRESHOLD = 50;
export const MAX_SIMULTANEOUS_LIGHTS = 8;

// Aspect ratio definitions
export const ASPECT_RATIOS = {
  '16:10': { value: 16/10, label: '16:10' },
  '16:9': { value: 16/9, label: '16:9' },
  '4:3': { value: 4/3, label: '4:3' },
  '21:9': { value: 21/9, label: '21:9' },
  '1:1': { value: 1, label: '1:1' }
};

// 90-degree lens mount gap
export const LENS90_MOUNT_GAP = 0.01;

/**
 * Configure material for multi-projector support.
 */
export const configureMaterialForProjectors = (material) => {
  if (material && typeof material.maxSimultaneousLights !== 'undefined') {
    material.maxSimultaneousLights = MAX_SIMULTANEOUS_LIGHTS;
  }
  return material;
};

/**
 * Parse an MTL file string into a Babylon.js StandardMaterial.
 */
export const parseMTLToMaterial = (mtlText, name, scene) => {
  const mat = new BABYLON.StandardMaterial(name, scene);
  mat.diffuseColor = new BABYLON.Color3(0.8, 0.8, 0.8);
  mat.specularColor = new BABYLON.Color3(0.1, 0.1, 0.1);
  mat.ambientColor = new BABYLON.Color3(0.2, 0.2, 0.2);
  mat.emissiveColor = new BABYLON.Color3(0, 0, 0);
  mat.specularPower = 25;
  mat.alpha = 1.0;

  const lines = mtlText.split('\n');
  for (const raw of lines) {
    const line = raw.trim();
    if (!line || line.startsWith('#')) continue;
    const parts = line.split(/\s+/);
    const key = parts[0];

    switch (key) {
      case 'Kd':
        if (parts.length >= 4) {
          mat.diffuseColor = new BABYLON.Color3(parseFloat(parts[1]), parseFloat(parts[2]), parseFloat(parts[3]));
          mat.emissiveColor = mat.diffuseColor.scale(0.15);
        }
        break;
      case 'Ka':
        if (parts.length >= 4) {
          mat.ambientColor = new BABYLON.Color3(parseFloat(parts[1]), parseFloat(parts[2]), parseFloat(parts[3]));
        }
        break;
      case 'Ks':
        if (parts.length >= 4) {
          mat.specularColor = new BABYLON.Color3(parseFloat(parts[1]), parseFloat(parts[2]), parseFloat(parts[3]));
        }
        break;
      case 'Ke':
        if (parts.length >= 4) {
          mat.emissiveColor = new BABYLON.Color3(parseFloat(parts[1]), parseFloat(parts[2]), parseFloat(parts[3]));
        }
        break;
      case 'Ns':
        if (parts.length >= 2) {
          mat.specularPower = parseFloat(parts[1]);
        }
        break;
      case 'd':
        if (parts.length >= 2) {
          mat.alpha = parseFloat(parts[1]);
        }
        break;
      case 'Tr':
        if (parts.length >= 2) {
          mat.alpha = 1.0 - parseFloat(parts[1]);
        }
        break;
      case 'illum':
        if (parts.length >= 2) {
          const illum = parseInt(parts[1]);
          if (illum === 0 || illum === 1) {
            mat.specularColor = new BABYLON.Color3(0, 0, 0);
          }
        }
        break;
      default:
        break;
    }
  }

  return mat;
};

/**
 * Get projector direction and up vector, accounting for 90-degree lens.
 */
export const getProjectorDirectionAndUp = (projector) => {
  const rotX = BABYLON.Tools.ToRadians(projector.rotation?.x || 0);
  const rotY = BABYLON.Tools.ToRadians(projector.rotation?.y || 0);
  const rotZ = BABYLON.Tools.ToRadians(projector.rotation?.z || 0);
  const rotMatrix = BABYLON.Matrix.RotationYawPitchRoll(rotY, rotX, rotZ);

  if (projector.lensType === 'lens90') {
    const rot = projector.lens90Rotation || 'right';
    let localDir;
    switch (rot) {
      case 'left':   localDir = new BABYLON.Vector3(-1, 0, 0); break;
      case 'right':  localDir = new BABYLON.Vector3( 1, 0, 0); break;
      case 'top':    localDir = new BABYLON.Vector3( 0, 1, 0); break;
      case 'bottom': localDir = new BABYLON.Vector3( 0,-1, 0); break;
      default:       localDir = new BABYLON.Vector3( 1, 0, 0); break;
    }
    let localUp;
    if (rot === 'top') localUp = new BABYLON.Vector3(0, 0, 1);
    else if (rot === 'bottom') localUp = new BABYLON.Vector3(0, 0, -1);
    else localUp = new BABYLON.Vector3(0, 1, 0);

    const direction = BABYLON.Vector3.TransformNormal(localDir, rotMatrix).normalize();
    const up = BABYLON.Vector3.TransformNormal(localUp, rotMatrix).normalize();
    return { direction, up, rotX, rotY, rotZ, rotMatrix };
  }

  if (projector.lensType === 'topBottom') {
    // Periscope folds back; lens cube faces BACKWARD (-Z local). Vertical
    // bias for the projected image is applied via lensShiftV in the renderer
    // (NOT here) — keeps direction consistent with the beam wireframe.
    const lensShiftH = (projector.lens_shift_h || 0) / 100;
    const baseDir = new BABYLON.Vector3(-lensShiftH * 0.5, 0, -1).normalize();
    const direction = BABYLON.Vector3.TransformNormal(baseDir, rotMatrix).normalize();
    const up = BABYLON.Vector3.TransformNormal(new BABYLON.Vector3(0, 1, 0), rotMatrix);
    return { direction, up, rotX, rotY, rotZ, rotMatrix };
  }

  if (projector.lensType === 'ust') {
    // UST rear-throw: lens at FRONT of body, beam exits STRAIGHT BACK along
    // -Z (horizontal). Vertical bias for the projected image is applied via
    // lensShiftV in the renderer — NOT here — to keep direction consistent
    // with the beam wireframe (which doesn't tilt direction either).
    const lensShiftH = (projector.lens_shift_h || 0) / 100;
    const baseDir = new BABYLON.Vector3(
      lensShiftH * 0.5,
      0,
      -1
    ).normalize();
    const baseUp = new BABYLON.Vector3(0, 1, 0);
    const direction = BABYLON.Vector3.TransformNormal(baseDir, rotMatrix).normalize();
    const up = BABYLON.Vector3.TransformNormal(baseUp, rotMatrix);
    return { direction, up, rotX, rotY, rotZ, rotMatrix };
  }

  // Standard lens
  const lensShiftH = (projector.lens_shift_h || 0) / 100;
  const lensShiftV = (projector.lens_shift_v || 0) / 100;
  const baseDir = new BABYLON.Vector3(lensShiftH * 0.5, lensShiftV * 0.5, 1).normalize();
  const direction = BABYLON.Vector3.TransformNormal(baseDir, rotMatrix).normalize();
  const up = BABYLON.Vector3.TransformNormal(new BABYLON.Vector3(0, 1, 0), rotMatrix);
  return { direction, up, rotX, rotY, rotZ, rotMatrix };
};

/**
 * Get the projection origin point (where light exits the lens).
 */
export const getProjectionOrigin = (projector, meshesRef) => {
  const pos = new BABYLON.Vector3(
    projector.position?.x || 0,
    projector.position?.y || 0,
    projector.position?.z || 0
  );

  if (projector.lensType !== 'lens90' && projector.lensType !== 'topBottom') {
    // Standard lens: offset from body center to lens front face + small forward push
    // The extra 0.005m (5mm) prevents the projector body from self-occluding its projection
    const bodyDepthMm = projector.body_depth || projector.spec?.depth || 600;
    const lensOffset = (bodyDepthMm / 1000) / 2 + 0.005;
    const rotX = BABYLON.Tools.ToRadians(projector.rotation?.x || 0);
    const rotY = BABYLON.Tools.ToRadians(projector.rotation?.y || 0);
    const rotZ = BABYLON.Tools.ToRadians(projector.rotation?.z || 0);
    const rotMatrix = BABYLON.Matrix.RotationYawPitchRoll(rotY, rotX, rotZ);
    const forward = BABYLON.Vector3.TransformNormal(new BABYLON.Vector3(0, 0, 1), rotMatrix).normalize();
    return pos.add(forward.scale(lensOffset));
  }

  if (projector.lensType === 'topBottom') {
    // Periscope U-shape (compact): pipe1 forward 0.20 + pipe2 vertical 0.30 +
    // pipe3 back (bodyD*0.55 + 0.20) + lens cube 0.18. Lens exit = back face.
    //
    // SINGLE SOURCE OF TRUTH for the Periscope projection apex. Includes the
    // -50mm Y, +160mm Z body-local fine-tune nudge that was previously
    // applied separately to the beam mesh — now baked in here.
    const bodyDepthMm = projector.body_depth || projector.spec?.depth || 600;
    const halfD = (bodyDepthMm / 1000) / 2;
    const pipe1L   = 0.20;
    const pipe2L   = 0.30;
    const pipe3L   = ((bodyDepthMm / 1000) * 0.55 + pipe1L) * 0.7;
    const cubeSize = 0.27;
    const pivotZ   = halfD + 0.01;

    const rotX = BABYLON.Tools.ToRadians(projector.rotation?.x || 0);
    const rotY = BABYLON.Tools.ToRadians(projector.rotation?.y || 0);
    const rotZ = BABYLON.Tools.ToRadians(projector.rotation?.z || 0);
    const rotMatrix = BABYLON.Matrix.RotationYawPitchRoll(rotY, rotX, rotZ);

    const dir = projector.topBottomDirection || 'top';
    const verticalSign = (dir === 'bottom') ? -1 : 1;
    // Local position of the lens-cube back face (where light exits) PLUS
    // the periscope fine-tune nudge: 0 Y (was -0.05, then user shift +50mm),
    // +160mm Z body-local.
    const localOffset = new BABYLON.Vector3(
      0,
      verticalSign * pipe2L,
      pivotZ + (pipe1L - pipe3L - cubeSize - 0.005) + 0.16
    );
    const worldOffset = BABYLON.Vector3.TransformNormal(localOffset, rotMatrix);
    return pos.add(worldOffset);
  }

  // Mesh-based path
  const pipe1Key = 'lens90pipe1_' + projector.id;
  if (meshesRef && meshesRef[pipe1Key]) {
    const pipe1 = meshesRef[pipe1Key];
    const rot = projector.lens90Rotation || 'right';
    let tipLocal;
    switch (rot) {
      case 'top':    tipLocal = new BABYLON.Vector3(0, 0.15, -0.20); break;
      case 'bottom': tipLocal = new BABYLON.Vector3(0, 0.15, 0.20); break;
      case 'right':  tipLocal = new BABYLON.Vector3(0.20, 0.15, 0); break;
      case 'left':   tipLocal = new BABYLON.Vector3(-0.20, 0.15, 0); break;
      default:       tipLocal = new BABYLON.Vector3(0.20, 0.15, 0); break;
    }
    return BABYLON.Vector3.TransformCoordinates(tipLocal, pipe1.getWorldMatrix());
  }

  // Math fallback
  const rotX = BABYLON.Tools.ToRadians(projector.rotation?.x || 0);
  const rotY = BABYLON.Tools.ToRadians(projector.rotation?.y || 0);
  const rotZ = BABYLON.Tools.ToRadians(projector.rotation?.z || 0);
  const rotMatrix = BABYLON.Matrix.RotationYawPitchRoll(rotY, rotX, rotZ);
  const forward = BABYLON.Vector3.TransformNormal(new BABYLON.Vector3(0, 0, 1), rotMatrix).normalize();

  const bodyD = (projector.body_depth || projector.spec?.depth || 600) / 1000;
  const pivotWorldPos = pos.add(forward.scale(bodyD / 2 + LENS90_MOUNT_GAP));

  // Projection apex = end of pipe1 (forward 0.30 from front-face pivot) PLUS
  // a +100mm shift along the LENS EXIT DIRECTION (body-local, per
  // lens90Rotation) and +20mm along body Z. The exit-direction nudge moves
  // the apex 100mm out of the body in the beam direction for ALL rotations
  // (right/left/top/bottom) — body-local X would only work for "right".
  // SINGLE SOURCE OF TRUTH for the lens90 apex — beam wireframe, projection
  // matrix, surface overlay and POV camera all share it.
  const rot = projector.lens90Rotation || 'right';
  let exitDirLocal;
  switch (rot) {
    case 'left':   exitDirLocal = new BABYLON.Vector3(-1, 0, 0); break;
    case 'right':  exitDirLocal = new BABYLON.Vector3( 1, 0, 0); break;
    case 'top':    exitDirLocal = new BABYLON.Vector3(0,  1, 0); break;
    case 'bottom': exitDirLocal = new BABYLON.Vector3(0, -1, 0); break;
    default:       exitDirLocal = new BABYLON.Vector3( 1, 0, 0); break;
  }
  const lens90NudgeLocal = exitDirLocal.scale(0.10).add(new BABYLON.Vector3(0, 0, 0.02));
  const lens90NudgeWorld = BABYLON.Vector3.TransformNormal(lens90NudgeLocal, rotMatrix);
  return pivotWorldPos.add(forward.scale(0.30)).add(lens90NudgeWorld);
};
